import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Cookies } from 'react-cookie-consent';

const LanguageContext = createContext(null);

/**
* In-memory translation cache
* Prevents repeated downloads
*/

const translationCache = new Map();

export const useLanguage = () => {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }

  return context;
};

const getInitialLocale = () => {
  try {
    const savedLanguage = Cookies.get('locale')
    if (savedLanguage) {
      return {
        language: savedLanguage,
      };
    }

    return {
      language: "en",
    };
  } catch {
    return {
      language: "en",
    };
  }
};

export const LanguageProvider = ({ children, fetchedBucketUrl, useLocalTranslations = false }) => { 
  const bucketUrl = fetchedBucketUrl || Cookies.get("bucketUrl");
  console.log(fetchedBucketUrl, 'fetchedBucketUrl in LanguageProvider')
  const [locale, setLocale] = useState(getInitialLocale);
  const [messages, setMessages] = useState({});
  const [loading, setLoading] = useState(true);
  const { language } = locale;

  const getTranslationUrl = useCallback(
    (lang) => {
      console.log('getTranslationUrl called with lang:', lang);
      const normalizedLang = lang?.toLowerCase() || "en";

      return useLocalTranslations
        ? `/static/assets/Languages/${normalizedLang}.json`
        : `${bucketUrl}assets/apac/${normalizedLang}/translations/translate.json`;
    },
    [bucketUrl, useLocalTranslations]
  );

  /**
   * Fetch translations from Cloudflare CDN
  */

  const fetchTranslations = useCallback(
    async (lang) => {
      const normalizedLang = lang?.toLowerCase() || "en";

      if (translationCache.has(normalizedLang)) {
        console.log(
          `Loaded ${normalizedLang} from memory cache`
        );

        setMessages(
          translationCache.get(normalizedLang)
        );

        return;
      }

      try {
        setLoading(true);

        const response = await fetch(
          getTranslationUrl(normalizedLang),
          {
            cache: "force-cache",
          }
        );

        if (!response.ok) {
          throw new Error(
            `Failed to load ${normalizedLang}.json`
          );
        }

        const data = await response.json();

        translationCache.set(
          normalizedLang,
          data
        );

        setMessages(data);

        console.log(
          `Loaded ${normalizedLang} from network`
        );
      } catch (error) {
        console.error(
          `Failed to load ${normalizedLang}.json`,
          error
        );
      } finally {
        setLoading(false);
      }
    },
    [getTranslationUrl]
  );

  /**
   * Load translations whenever language changes
  */

  useEffect(() => {
    document.documentElement.lang = language;
    fetchTranslations(language);
  }, [language, fetchTranslations]);

  /**
   * Persist locale
  */

  // useEffect(() => {
  //   document.documentElement.lang = language;
  // }, [language]);

  /**
   * Update language/country together
  */

  const updateLocale = useCallback(
    ({
      language: newLanguage,
    }) => {
      setLocale((prev) => ({
        ...prev,
        ...(newLanguage && {
          language: newLanguage.toLowerCase(),
        }),
      }));
    },
    [],
  );

  /**
   * Set language directly
  */

  const setLanguage = useCallback(
    (lang) => {
      updateLocale({
        language: lang,
      });
    },
    [updateLocale],
  );

  /**
   * Translation helper
  */

  const t = useCallback(
    (key, fallback = key) => messages?.[key] ?? fallback,
    [messages],
  );

  const value = useMemo(
    () => ({
      language,
      loading,
      setLanguage,
      updateLocale,
      t,
    }),
    [language, loading, setLanguage, updateLocale, t],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};